import { FC, useEffect, useState } from 'react'
import { Box, Collapse } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { Icon } from 'packages/eid-icons'
import { useTranslation } from 'react-i18next'
import { useResourceTypeContext } from 'resourceTypeContext'

export type PreApprovedItemProps = {
    title: string
    itemCount?: number
    defaultExpand?: any
    showExpand?: any
    infoSubtext?: string
    boldHeader?: boolean
    accessKeyType?: string
    children?: React.ReactNode
}

const PaddedDiv = styled(Box)({
    margin: '0 20px',
})

const StyledRoot = styled(Box)(({ theme }) => ({
    background: '#fbfbfd',
    marginBottom: '16px',
}))

const StyledAccordion = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 35px',
    borderBottom: '1px solid #d8dadd',
    background: 'transparent',
}))

const StyledAccordionLeft = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
})

const StyledHeading = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isBold',
})<{ isBold?: boolean }>(({ isBold }) => ({
    fontSize: '14px',
    color: '#282828',
    ...(isBold && {
        fontWeight: 'bold',
    }),
}))

const StyledCount = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isInfoSubtext',
})<{ isInfoSubtext?: boolean }>(({ theme, isInfoSubtext }) => ({
    padding: '0px 8px',
    marginLeft: '8px',
    background: theme.palette.grey[900],
    borderRadius: '8px',
    fontSize: isInfoSubtext ? '10px' : '14px',
    color: theme.palette.common.white,
    ...(isInfoSubtext && {
        marginTop: '5px',
    }),
}))

const StyledExpandIcon = styled(Box)(({ theme }) => ({
    width: '1.6rem',
    height: '1.6rem',
    marginLeft: '0.8rem',
    marginTop: '-0.1rem',
    color: theme.palette.primary.main,
}))

const StyledExpandButton = styled(Box)({
    textTransform: 'uppercase',
    '&:hover': {
        cursor: 'pointer',
    },
})

export const PreApprovedItemWrapper: FC<PreApprovedItemProps> = (props) => {
    const {
        title,
        itemCount,
        children,
        defaultExpand = true,
        showExpand = true,
        infoSubtext = '',
        boldHeader = false,
        accessKeyType = '',
    } = props
    const { t } = useTranslation()
    const [
        {
            selectdAccessLevelAppRoleRight: {
                selectedAppRoleOrRightAlreadyAssigned,
                accessType,
            },
        },
    ]: any = useResourceTypeContext()
    const [showExpanded, setShowExpanded] = useState<boolean>(defaultExpand)
    const theme = useTheme()

    useEffect(() => {
        if (
            selectedAppRoleOrRightAlreadyAssigned &&
            accessKeyType === accessType
        ) {
            setShowExpanded(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAppRoleOrRightAlreadyAssigned])

    return (
        <>
            <StyledAccordion>
                <StyledAccordionLeft>
                    <StyledHeading isBold={boldHeader}>
                        {title ? title : '-'}
                    </StyledHeading>
                    {itemCount ? (
                        <StyledCount>{itemCount}</StyledCount>
                    ) : infoSubtext ? (
                        <StyledCount isInfoSubtext>{infoSubtext}</StyledCount>
                    ) : null}
                </StyledAccordionLeft>
                <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    fontSize="12px"
                    fontWeight="bold"
                    color={theme?.palette?.primary?.main}
                >
                    {showExpand && (
                        <StyledExpandButton
                            sx={{ display: 'flex' }}
                            onClick={() => setShowExpanded(!showExpanded)}
                            tabIndex={0}
                            role="button"
                            aria-expanded={showExpanded}
                            aria-label={showExpanded ? t('Hide') : t('Show')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    setShowExpanded((p) => !p)
                                }
                            }}
                        >
                            {showExpanded ? t('Hide') : t('Show')}
                            <StyledExpandIcon>
                                {showExpanded ? (
                                    <Icon
                                        name="MinusCircle"
                                        width="1.6rem"
                                        height="1.6rem"
                                    />
                                ) : (
                                    <Icon
                                        name="PlusCircle"
                                        width="1.6rem"
                                        height="1.6rem"
                                    />
                                )}
                            </StyledExpandIcon>
                        </StyledExpandButton>
                    )}
                </Box>
            </StyledAccordion>
            <Collapse in={showExpanded} timeout={400} unmountOnExit>
                <PaddedDiv>
                    <StyledRoot>{children}</StyledRoot>
                </PaddedDiv>
            </Collapse>
        </>
    )
}
